/**
 * Scroll Manager Hook
 * Manages scroll behavior for chat messages with:
 * - Auto-scroll to bottom for new messages (when near bottom)
 * - New message indicator when scrolled up
 * - Scroll to specific message
 * - Virtual scrolling support
 */

const ScrollManagerHook = {
  mounted() {
    this.messageContainer = this.el;
    this.isNearBottom = true;
    this.lastScrollTop = 0;
    this.newMessageIndicator = null;
    this.scrollThreshold = 100; // pixels from bottom to consider "near bottom"

    // Initial scroll to bottom
    this.scrollToBottom();

    // Track scroll position
    this.messageContainer.addEventListener('scroll', this.handleScroll.bind(this));

    // Handle LiveView events
    this.handleEvent('new_message', () => {
      if (this.isNearBottom) {
        requestAnimationFrame(() => this.scrollToBottom());
      } else {
        this.showNewMessageIndicator();
      }
    });

    this.handleEvent('scroll_to_bottom', () => {
      this.scrollToBottom();
      this.hideNewMessageIndicator();
    });

    this.handleEvent('scroll_to_message', ({ message_id }) => {
      this.scrollToMessage(message_id);
    });

    this.handleEvent('messages_loaded', () => {
      // Scroll to bottom after messages are loaded
      requestAnimationFrame(() => {
        this.scrollToBottom();
      });
    });

    // Handle prepended messages (infinite scroll up)
    this.handleEvent('messages_prepended', ({ scroll_to_id }) => {
      if (scroll_to_id) {
        this.scrollToMessage(scroll_to_id, false);
      }
    });
  },

  destroyed() {
    this.hideNewMessageIndicator();
  },

  handleScroll() {
    const { scrollTop, scrollHeight, clientHeight } = this.messageContainer;
    const position = scrollTop + clientHeight;
    const maxScroll = scrollHeight;

    this.isNearBottom = position >= maxScroll - this.scrollThreshold;
    this.lastScrollTop = scrollTop;

    // Hide indicator if scrolled to bottom
    if (this.isNearBottom) {
      this.hideNewMessageIndicator();
    }

    // Load more messages when scrolled to top
    if (scrollTop < 50) {
      this.loadMoreMessages();
    }
  },

  scrollToBottom(smooth = true) {
    const behavior = smooth ? 'smooth' : 'auto';
    this.messageContainer.scrollTo({
      top: this.messageContainer.scrollHeight,
      behavior
    });
    this.isNearBottom = true;
  },

  scrollToMessage(messageId, highlight = true) {
    const message = document.getElementById(`message-${messageId}`) ||
                   document.querySelector(`[data-message-id="${messageId}"]`);

    if (message) {
      message.scrollIntoView({ behavior: 'smooth', block: 'center' });

      if (highlight) {
        message.classList.add('highlight', 'animate-pulse', 'bg-isx-babyblue-600/20');
        setTimeout(() => {
          message.classList.remove('highlight', 'animate-pulse', 'bg-isx-babyblue-600/20');
        }, 2000);
      }
    }
  },

  showNewMessageIndicator() {
    if (this.newMessageIndicator) return;

    this.newMessageIndicator = document.createElement('button');
    this.newMessageIndicator.className = `
      fixed left-1/2 transform -translate-x-1/2
      bg-green-500 hover:bg-green-600
      text-white font-medium
      px-4 py-2 rounded-full
      shadow-lg cursor-pointer
      flex items-center gap-2
      transition-all duration-200
      animate-bounce
      z-50
    `.trim().replace(/\s+/g, ' ');

    this.newMessageIndicator.style.bottom = 'calc(var(--footer-nav-offset, 5rem) + 1rem)';

    this.newMessageIndicator.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clip-rule="evenodd" />
      </svg>
      New messages
    `;

    this.newMessageIndicator.onclick = () => {
      this.scrollToBottom();
      this.hideNewMessageIndicator();
    };

    this.el.parentElement.appendChild(this.newMessageIndicator);

    // Auto-hide after 10 seconds
    this.indicatorTimeout = setTimeout(() => {
      this.hideNewMessageIndicator();
    }, 10000);
  },

  hideNewMessageIndicator() {
    if (this.newMessageIndicator) {
      this.newMessageIndicator.remove();
      this.newMessageIndicator = null;
    }
    if (this.indicatorTimeout) {
      clearTimeout(this.indicatorTimeout);
      this.indicatorTimeout = null;
    }
  },

  loadMoreMessages() {
    // Debounce load more requests
    if (this.loadMoreDebounce) return;

    this.loadMoreDebounce = true;
    this.pushEvent('load_more_messages', {});

    setTimeout(() => {
      this.loadMoreDebounce = false;
    }, 1000);
  }
};

export default ScrollManagerHook;
